import { TooltipContentProps } from '@radix-ui/react-tooltip';
import { ComponentProps, ComponentPropsWithoutRef, ComponentType, ElementType, JSX, Ref } from 'react';

import { Tooltip } from '../Tooltip/Tooltip';

import { ButtonWrapper } from './components/ButtonWrapper';
import { getButtonSize } from './util/getButtonSize';
import { getIconSize } from './util/getIconSize';

export interface IconButtonProps extends ComponentPropsWithoutRef<'button'> {
    Icon: ComponentType<{ width?: number; height?: number }>;
    emphasis?: 'medium' | 'none' | 'transparent';
    size?: 'small' | 'medium';
    destructive?: boolean;
    variant?: 'default' | 'rounded';
    tooltipLabel?: string;
    tooltipSide?: TooltipContentProps['side'];
    ref?: Ref<HTMLButtonElement>;
}

const BaseIconButton = ({
    Icon,
    emphasis = 'none',
    size = 'medium',
    destructive = false,
    variant = 'default',
    tooltipLabel,
    tooltipSide,
    ref,
    ...rest
}: IconButtonProps) => {
    const iconSize = getIconSize(size);

    const IconButtonContent = (
        <ButtonWrapper
            ref={ref}
            size={getButtonSize(size)}
            destructive={destructive}
            emphasis={emphasis}
            variant={variant}
            {...rest}
        >
            <Icon width={iconSize} height={iconSize} />
        </ButtonWrapper>
    );

    if (tooltipLabel !== undefined) {
        return (
            <Tooltip side={tooltipSide} label={tooltipLabel}>
                {IconButtonContent}
            </Tooltip>
        );
    }

    return IconButtonContent;
};

BaseIconButton.displayName = 'IconButton';

interface IconButtonComponentType {
    <C extends ElementType = 'button'>(
        props: IconButtonProps &
            Omit<ComponentProps<C>, keyof IconButtonProps> & {
                as?: C;
            }
    ): JSX.Element;

    displayName?: string;
}

export const IconButton = BaseIconButton as IconButtonComponentType;
